import { iteroErrorMessage } from '../../../../services/iteroConnection';
import { IteroBrowserModal } from '../itero-browser/IteroBrowserModal';
import { IteroActionButtons } from './IteroActionButtons';
import type { IteroBoxAccess } from './iteroBoxActions';
import { IteroConnectModal } from './IteroConnectModal';
import { IteroDisconnectModal } from './IteroDisconnectModal';
import { IteroStatusLine, IteroStatusSkeleton, IteroTestResultLine } from './IteroStatusLine';
import { STATUS_NEEDS_VIEW_ACCESS } from './iteroConnectionCopy';
import { useIteroConnectionBox, type IteroConnectionBoxState } from './useIteroConnectionBox';

interface IteroBoxDialogsProps {
    box: IteroConnectionBoxState;
    practiceId: string;
    practiceName: string;
}

function IteroBoxDialogs({ box, practiceId, practiceName }: IteroBoxDialogsProps) {
    const { connect, disconnect } = box.requests;
    return (
        <>
            {box.isConnectOpen && (
                <IteroConnectModal
                    practiceId={practiceId}
                    isPending={connect.isPending}
                    connectError={connect.error}
                    onConnect={box.connectClinic}
                    onCancel={box.closeConnect}
                />
            )}
            {box.disconnectMessage !== null && (
                <IteroDisconnectModal
                    message={box.disconnectMessage}
                    isPending={disconnect.isPending}
                    onConfirm={box.confirmDisconnect}
                    onCancel={box.cancelDisconnect}
                />
            )}
            {box.isBrowseOpen && box.clinicId !== null && (
                <IteroBrowserModal
                    practiceId={practiceId}
                    practiceName={practiceName}
                    clinicId={box.clinicId}
                    onClose={box.closeBrowse}
                />
            )}
        </>
    );
}

interface IteroConnectionBoxProps {
    practiceId: string;
    practiceName: string;
    access: IteroBoxAccess;
}

export function IteroConnectionBox({ practiceId, practiceName, access }: IteroConnectionBoxProps) {
    const box = useIteroConnectionBox(practiceId, access);
    const { status, test, refresh } = box.requests;

    return (
        <div className="flex flex-col gap-2 min-w-0 border border-gray-200 rounded-lg bg-gray-50 px-3 py-2.5 w-full">
            {!access.canView && (
                <span className="text-[12px] text-gray-500">{STATUS_NEEDS_VIEW_ACCESS}</span>
            )}
            {status.isLoading && <IteroStatusSkeleton />}
            {status.isError && !status.isFetching && (
                <span className="text-[12px] text-red-600 break-words">
                    {iteroErrorMessage(status.error, "Couldn't load the iTero status")}
                </span>
            )}
            {status.data && <IteroStatusLine status={status.data} />}

            <IteroActionButtons
                actions={box.actions}
                disabled={box.isBusy}
                pending={box.pending}
                onAction={box.runAction}
                onRetry={status.isError && !status.data ? () => void status.refetch() : undefined}
            />

            {test.isError && !test.isPending && (
                <span className="text-[12px] font-medium text-red-600 break-words">
                    {`Test failed: ${iteroErrorMessage(test.error, 'the request did not complete')}`}
                </span>
            )}
            {test.isSuccess && !test.isPending && <IteroTestResultLine live={test.data.live} />}

            {(test.isPending || refresh.isPending) && (
                <span className="text-[11px] text-gray-500">This can take a few seconds…</span>
            )}

            <IteroBoxDialogs box={box} practiceId={practiceId} practiceName={practiceName} />
        </div>
    );
}
